import {
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  CustomTable,
  InputControl,
  LoadingButton,
  PageListLayout,
} from "../../../shared";
import { useMemo } from "react";
import { Add, RemoveCircle } from "@mui/icons-material";
import { useManageFutureCost } from "../hooks/useManageFutureCost";
import { AddIngredientDialog } from "../components";
import { futureCostValidationRules } from "../hooks";
import { addDays } from "date-fns";
import { formatCost } from "../../../utils";
export const ManageFutureCost = () => {
  const {
    state,
    isSubmitting,
    formValidator,
    taxListQuery,
    dispatch,
    toggleAddIngredientDialog,
    onChange,
    onSubmit,
  } = useManageFutureCost();
  const navigate = useNavigate();

  // Define breadcrumbs for navigation
  const breadcrumbs = [
    <Link key="1" to="/future-cost" style={{ textDecoration: "none" }}>
      Future Costing - Ingredients
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Future Costing - Ingredients
    </Typography>,
  ];

  // Helper text for the costing date field
  const dateHelperText = formValidator.current.message(
    "costing_date",
    state.formData.date,
    "required|notPastDate",
    {
      messages: {
        required: "Date is required",
        after: "Date must be in the future.",
      },
    },
  );

  // Define columns for the table
  const columns = useMemo(
    () => [
      {
        header: "Ingredient Name",
        accessorKey: "name",
        cell: ({ getValue, row: { original } }) => {
          const hasError = state.validationErrors.includes(original.id);
          return (
            <>
              <Typography
                onClick={() => navigate(`/ingredients/details/${original.id}`)}
                variant="subtitle2"
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "neutral.10",
                  maxWidth: "300px",
                }}
              >
                {getValue()}
              </Typography>
              {hasError && (
                <FormHelperText
                  sx={{
                    color: "error.main",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Already have future costing for the specified date
                </FormHelperText>
              )}
            </>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => {
          const status = getValue();

          return status ? (
            <Chip
              size="small"
              color="primary"
              variant="filled"
              label="ACTIVE"
            />
          ) : (
            <Chip size="small" color="gray" label="INACTIVE" />
          );
        },
      },
      { header: "Ingredient Type", accessorKey: "type" },
      { header: "Tax Group", accessorKey: "tax_group_name" },
      { header: "Buying Unit", accessorKey: "buying_unit" },
      {
        header: "Cur Cost ex gst($)",
        accessorKey: "cost_exc_tax",
        cell: ({ getValue }) => `$${formatCost(getValue())}`,
      },
      {
        header: "Cur Cost in gst($)",
        accessorKey: "cost_inc_tax",
        cell: ({ getValue }) => `$${formatCost(getValue())}`,
      },
      {
        header: "Future Cost ex gst($)",
        accessorKey: "future_cost_exc_gst",
        cell: ({ row }) => {
          const { original } = row;
          const name = "future_cost_exc_gst" + original.id;
          const helperText = formValidator.current.message(
            name,
            original.future_cost_exc_gst,
            futureCostValidationRules.rules,
            futureCostValidationRules.messages,
          );

          return (
            <InputControl
              name={name}
              size="small"
              type="number"
              value={original.future_cost_exc_gst}
              onChange={(event) =>
                onChange(event, original?.tax_group_id, row.index)
              }
              error={!!helperText}
              helperText={helperText}
            />
          );
        },
      },
      {
        header: "Future Cost inc gst($)",
        accessorKey: "future_cost_inc_gst",
        cell: ({ row }) => {
          const { original } = row;
          const name = "future_cost_inc_gst" + original.id;

          const helperText = formValidator.current.message(
            name,
            original.future_cost_inc_gst,
            futureCostValidationRules.rules,
            futureCostValidationRules.messages,
          );
          return (
            <InputControl
              name="future_cost_inc_gst"
              size="small"
              type="number"
              value={original.future_cost_inc_gst}
              onChange={(event) =>
                onChange(event, original?.tax_group_id, row.index)
              }
              error={!!helperText}
              helperText={helperText}
            />
          );
        },
      },
      {
        id: "id",
        cell: ({ row: { original } }) => {
          return (
            <IconButton
              size="small"
              onClick={() =>
                dispatch({
                  type: "REMOVE_ITEM",
                  payload: { id: original.id },
                })
              }
            >
              <RemoveCircle color="error" />
            </IconButton>
          );
        },
      },
    ],
    [taxListQuery.isPending, state.validationErrors],
  );

  return (
    <PageListLayout
      headerText="Future Costing - Ingredients"
      breadcrumbs={breadcrumbs}
      rightContent={
        <LoadingButton
          variant="contained"
          size="small"
          onClick={onSubmit}
          disabled={state.formData.data.length === 0}
          loading={isSubmitting}
        >
          Save
        </LoadingButton>
      }
    >
      <CustomTable
        columns={columns}
        defaultData={state.formData.data}
        isInTab
        hideEmptyContent
        isPagination={false}
        isTableHeader={false}
        tableHeaderContent={
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <InputControl
              type="datePicker"
              size="small"
              label="Date"
              helperText={dateHelperText}
              error={!!dateHelperText}
              customDatePickerIcon
              datePickerProps={{
                disableMaskedInput: true,
                inputFormat: "dd, MMM yyyy",
                minDate: addDays(new Date(), 1),
                value: state.formData.date,
                onChange: (event) =>
                  onChange({ target: { name: "date", value: event } }),
              }}
            />
          </Stack>
        }
        tableStyle={{
          border: "1px solid",
          borderColor: "divider",
        }}
        tableFooter={
          <Box sx={{ backgroundColor: "background.paper", p: "4px" }}>
            <Button
              startIcon={<Add />}
              size="small"
              onClick={toggleAddIngredientDialog}
            >
              Add Ingredient
            </Button>
          </Box>
        }
      />

      {state.dialog.addIngredient !== null && (
        <AddIngredientDialog
          open={state.dialog.addIngredient}
          existingIngredients={
            state.formData.data.length > 0
              ? state.formData.data.map((d) => d.id)
              : []
          }
          onAdd={(selectedItems) => {
            dispatch({
              type: "ADD_INGREDIENTS",
              payload: selectedItems,
            });
            toggleAddIngredientDialog(null);
          }}
          onCancel={() => toggleAddIngredientDialog(null)}
        />
      )}
    </PageListLayout>
  );
};
